import React from 'react'
import styles from './CustomTable.module.css'
import IconButton from '../IconButton'
import StatusCell from './components/StatusCell'
import DomainCell from './components/DomainCell'
import CustomTableHeader from './components/CustomTableHeader'
import OverflowMenu from './components/OverflowMenu/OverflowMenu.js'
import SearchBar from './SearchBar/SearchBar.js'
import useCheckIsMobileScreen from '../../hooks/checkMobileScreen'
import MenuItem from './components/MenuItem'
import { Button } from 'react-bootstrap'
import CustomTooltip from '../CustomTooltip'

const TableHeader = ({ header }) => (
  <th className={header.cellType === 'views' ? styles.viewsHeader : ''}>
    <span className={styles.headerText}>{header.text}</span>
    {header.icon && <img src={header.icon} alt={header.text} className={styles.headerIcon} />}
  </th>
)

const TableCell = ({ content, cellType, isMobile, isTablet, onShare }) => {
  switch (cellType) {
    case 'status':
      return (
        <td className={styles.tableCell}>
          <StatusCell
            status={content?.status}
            text={content?.status || content?.text}
            passwordProtected={content.passwordProtected}
            emailProtected={content.emailProtected}
            ssoProtected={content.ssoProtected}
            className={content?.className}
          />
        </td>
      )
    case 'domain':
      return (
        <td className={styles.tableCell}>
          <DomainCell {...content} onShare={onShare} />
        </td>
      )
    case 'normal_text':
      return (
        <td className={styles.tableCell}>
          <div className={styles.normalText}>{content}</div>
        </td>
      )
    case 'actions':
      if (isMobile || isTablet) {
        return (
          <td className={styles.buttonGroup}>
            <MenuItem content={content} icon={'/assets/icons/menu_horizontal.svg'} />
          </td>
        )
      } else {
        return (
          <td className={styles.buttonGroup}>
            {content.map((button, index) => {
              if (button.type === 'action_menu') {
                return <OverflowMenu key={index} {...button.props} />
              }
              if (button.type === 'custom_button') {
                return <>{button.component}</>
              }
              return (
                <IconButton
                  key={index}
                  src={button.icon}
                  onClick={button.onClick}
                  disabled={button.disabled}
                  tooltip={button.tooltip}
                  link={button.link}
                  className={button.className || styles.actionButton}
                  label={button.label}
                  dataTestId={button.dataTestId}
                />
              )
            })}
          </td>
        )
      }

    case 'link':
      return (
        <td className={styles.tableCell}>
          <a href={content} target="_blank" rel="noopener noreferrer">
            {content}
          </a>
        </td>
      )
    case 'lastModified':
      return (
        <td>
          <div className={styles.normalText}>{content}</div>
        </td>
      )
    case 'views':
      return (
        <td className={`${styles.tableCell} ${styles.viewsCell}`}>
          <div className={styles.normalText}>
            {content?.count !== undefined && content?.count !== '-' ? (
              <CustomTooltip label={content.tooltip || ''} placement="top">
                <span className={styles.clickableViews} onClick={() => content.onClick?.()}>
                  {typeof content.count === 'number' ? content.count.toLocaleString() : content.count}
                </span>
              </CustomTooltip>
            ) : (
              <span>{content?.count ?? '-'}</span>
            )}
          </div>
        </td>
      )
    case 'project':
      return (
        <td className={styles.tableCell}>
          <div className={styles.normalText}>
            <div>{content.label}</div>
            {content.daysRemaining !== undefined && (
              <div className="text-danger small">({content.daysRemaining} days)</div>
            )}
          </div>
        </td>
      )
    case 'email':
      return (
        <td className={styles.tableCell}>
          <div className={styles.emailCell}>
            {content.hasEmail ? (
              <button
                className={`btn btn-link p-0 text-primary text-decoration-none d-flex align-items-center ${styles.emailButton}`}
                onClick={() => content.onViewEmails?.(content.domain)}
                aria-label={`View emails for ${content.domain}`}
              >
                <i className="fas fa-envelope text-success me-2"></i>
                <span>View emails</span>
              </button>
            ) : (
              <button
                className={`btn btn-link p-0 text-primary text-decoration-none d-flex align-items-center ${styles.emailButton}`}
                onClick={() => content.onAddEmail?.(content.domain)}
                aria-label={`Add email for ${content.domain}`}
              >
                <i className="fas fa-envelope me-2"></i>
                <span>Add email</span>
              </button>
            )}
          </div>
        </td>
      )
    default:
      if (React.isValidElement(content)) {
        return (
          <td>
            <span className={styles.normalText}>{content}</span>
          </td>
        )
      }
      return (
        <td>
          <span className={styles.normalText}>{String(content)}</span>
        </td>
      )
  }
}

const CustomTable = ({
  headers,
  data,
  title,
  badge,
  headerButtons,
  emptyStateMessage,
  emptyStateButton,
  emptyStateCustomElement,
  onSearch,
  searchTerm,
  showSearch = false,
  disabled,
  onShare,
  spaceId,
  spaceName,
  onEditSpace,
  onDeleteSpace,
  isSpace = false
}) => {
  const { isMobile, isTablet } = useCheckIsMobileScreen()
  return (
    <div className={styles.tableWrapper}>
      <CustomTableHeader
        title={title}
        badge={badge}
        buttons={headerButtons}
        spaceId={spaceId}
        spaceName={spaceName}
        onEditSpace={onEditSpace}
        onDeleteSpace={onDeleteSpace}
        isSpace={isSpace}
      />
      <SearchBar onSearch={onSearch} searchTerm={searchTerm} show={showSearch} />
      {data.length > 0 ? (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead className={styles.tableHeaderWrapper}>
              <tr className={styles.tableHeader}>
                {headers.map((header, index) => (
                  <TableHeader key={index} header={header} />
                ))}
              </tr>
            </thead>
            <tbody className={styles.tableBody}>
              {data.map((row, rowIndex) => (
                <tr key={rowIndex} className={`${styles.tableRow} custom-table-row`} tabIndex={0}>
                  {headers.map((header, cellIndex) => (
                    <TableCell
                      key={cellIndex}
                      content={row[header.key]}
                      cellType={header.cellType}
                      isMobile={isMobile}
                      isTablet={isTablet}
                      onShare={onShare}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles.emptyState}>
          <span className={styles.emptyStateMessage}>{emptyStateMessage || 'No data available'}</span>
          {emptyStateCustomElement && <div className={styles.emptyStateCustomElement}>{emptyStateCustomElement}</div>}
          {emptyStateButton && (
            <Button
              onClick={emptyStateButton.onClick}
              variant="outline-primary"
              size="lg"
              className={styles.uploadButton}
              disabled={disabled}
            >
              {emptyStateButton.text || 'Upload'}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default CustomTable
